import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Stack,
    TextField,
    Typography,
    Avatar,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Chip,
} from "@mui/material";
import { ArrowBack, Delete, Add, Save } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field } from "formik";

import { DefaultPage } from "../../layouts/default-page";
import { Notify } from "../../layouts/notify";
import {
    useAddMallMutation,
    useUpdateMallMutation,
    useGetMallByIdQuery,
    useGetMallTicketUrlsQuery,
    useAddMallTicketUrlMutation,
    useDeleteMallTicketUrlMutation,
} from "../../services/mall.query";
import { useGetCinemasQuery, useUpdateTheatreMutation } from "../../services/cinema.query";
import * as yup from "yup";

const mallValidationSchema = yup.object().shape({
    name: yup.string().required("Mall name is required"),
    street_address: yup.string().nullable(),
    barangay_address: yup.string().nullable(),
    city_address: yup.string().nullable(),
    province_address: yup.string().nullable(),
    code_pass: yup.string().required("Mall code is required"),
    logo_url: yup.string().nullable(),
    is_app_displayed: yup.boolean(),
});

export default function MallDetailsPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const isCreateMode = id === "new";

    const { data: mall, isLoading: mallLoading, refetch } = useGetMallByIdQuery(id!, {
        skip: isCreateMode || !id,
    });

    const { data: ticketUrls = [], refetch: refetchTicketUrls } = useGetMallTicketUrlsQuery(id!, {
        skip: isCreateMode || !id,
    });

    const { data: linkedTheatresData, isLoading: theatresLoading } = useGetCinemasQuery(
        {
            start: 0,
            end: 500,
            mallId: !isCreateMode ? id : undefined,
        },
        { skip: isCreateMode }
    );

    const linkedTheatres = linkedTheatresData?.list ?? [];

    const [addMall, { isLoading: adding }] = useAddMallMutation();
    const [updateMall, { isLoading: updating }] = useUpdateMallMutation();
    const [addTicketUrl, { isLoading: addingTicket }] = useAddMallTicketUrlMutation();
    const [deleteTicketUrl] = useDeleteMallTicketUrlMutation();
    const [updateTheatre] = useUpdateTheatreMutation();

    const [newTicketUrl, setNewTicketUrl] = useState("");

    const handleSave = async (values: any) => {
        try {
            if (isCreateMode) {
                const created = await addMall(values).unwrap();
                Notify("info", "Mall created successfully");
                navigate(`/malls/${created.id}`);
                return;
            }

            await updateMall({
                id,
                ...values,
            }).unwrap();

            Notify("info", "Mall updated successfully");
            refetch();
        } catch (error) {
            Notify("error", "Failed to save mall");
        }
    };

    const handleAddTicketUrl = async () => {
        if (!id || !newTicketUrl.trim()) return;

        try {
            await addTicketUrl({
                mall_id: id,
                url: newTicketUrl.trim(),
            }).unwrap();

            setNewTicketUrl("");
            refetchTicketUrls();
            Notify("info", "Ticket URL added successfully");
        } catch {
            Notify("error", "Failed to add ticket URL");
        }
    };

    const handleDeleteTicketUrl = async (ticketId: number | string) => {
        try {
            await deleteTicketUrl(String(ticketId)).unwrap();
            refetchTicketUrls();
            Notify("info", "Ticket URL removed successfully");
        } catch {
            Notify("error", "Failed to remove ticket URL");
        }
    };

    const handleUnlinkTheatre = async (theatreId: number | string) => {
        try {
            await updateTheatre({
                id: theatreId,
                mall_id: null,
            }).unwrap();

            Notify("info", "Theatre unlinked successfully");
            refetch();
        } catch {
            Notify("error", "Failed to unlink theatre");
        }
    };

    const initialValues = {
        name: mall?.name || "",
        street_address: mall?.street_address || "",
        barangay_address: mall?.barangay_address || "",
        city_address: mall?.city_address || "",
        province_address: mall?.province_address || "",
        code_pass: mall?.code_pass || "",
        logo_url: mall?.logo_url || "",
        is_app_displayed: mall?.is_app_displayed ?? true,
    };

    return (
        <DefaultPage title={isCreateMode ? "Add Mall" : "Manage Mall"} loading={mallLoading}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate("/movie-house/malls")}>
                    Back
                </Button>
            </Stack>

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={mallValidationSchema}
                onSubmit={handleSave}
            >
                {({ touched, errors, values, setFieldValue }) => (
                    <Form>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <Card sx={{ boxShadow: 4 }}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight="bold" mb={2}>
                                            Mall Information
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    name="name"
                                                    label="Mall Name"
                                                    error={touched.name && Boolean(errors.name)}
                                                    helperText={touched.name && errors.name}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    name="street_address"
                                                    label="Street"
                                                    error={touched.street_address && Boolean(errors.street_address)}
                                                    helperText={touched.street_address && errors.street_address}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    name="barangay_address"
                                                    label="Barangay"
                                                    error={touched.barangay_address && Boolean(errors.barangay_address)}
                                                    helperText={touched.barangay_address && errors.barangay_address}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    name="city_address"
                                                    label="City"
                                                    error={touched.city_address && Boolean(errors.city_address)}
                                                    helperText={touched.city_address && errors.city_address}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    name="province_address"
                                                    label="Province"
                                                    error={touched.province_address && Boolean(errors.province_address)}
                                                    helperText={touched.province_address && errors.province_address}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    name="code_pass"
                                                    label="Mall Code"
                                                    error={touched.code_pass && Boolean(errors.code_pass)}
                                                    helperText={touched.code_pass && errors.code_pass}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Field
                                                    as={TextField}
                                                    fullWidth
                                                    name="logo_url"
                                                    label="Logo URL"
                                                    error={touched.logo_url && Boolean(errors.logo_url)}
                                                    helperText={touched.logo_url && errors.logo_url}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel id="mall-status-label">Status</InputLabel>
                                                    <Select
                                                        labelId="mall-status-label"
                                                        label="Status"
                                                        value={values.is_app_displayed ? "active" : "inactive"}
                                                        onChange={(e) => {
                                                            setFieldValue("is_app_displayed", e.target.value === "active");
                                                        }}
                                                    >
                                                        <MenuItem value="active">Active</MenuItem>
                                                        <MenuItem value="inactive">Inactive</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>

                                        <Box mt={3} display="flex" justifyContent="flex-end">
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                startIcon={<Save />}
                                                disabled={adding || updating}
                                            >
                                                {isCreateMode ? "Create Mall" : "Update Mall"}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Card sx={{ boxShadow: 4 }}>
                                    <CardContent>
                                        <Typography variant="h6" fontWeight="bold" mb={2}>
                                            Preview
                                        </Typography>

                                        <Stack spacing={2} alignItems="center">
                                            <Avatar
                                                src={mall?.logo_url || ""}
                                                variant="rounded"
                                                sx={{ width: 100, height: 100 }}
                                            />
                                            <Typography fontWeight="bold" textAlign="center">
                                                {mall?.name || "New Mall"}
                                            </Typography>
                                            <Chip
                                                label={values.is_app_displayed ? "Active" : "Inactive"}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    bgcolor: values.is_app_displayed ? "#E8F5E9" : "#FFEBEE",
                                                    color: values.is_app_displayed ? "#2E7D32" : "#C62828",
                                                    border: `1px solid ${values.is_app_displayed ? "#A5D6A7" : "#EF9A9A"
                                                        }`,
                                                }}
                                            />

                                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                                {[
                                                    mall?.street_address,
                                                    mall?.barangay_address,
                                                    mall?.city_address,
                                                    mall?.province_address,
                                                ]
                                                    .filter(Boolean)
                                                    .join(", ") || "Address not available"}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {!isCreateMode && (
                                <>
                                    <Grid item xs={12}>
                                        <Card sx={{ boxShadow: 4 }}>
                                            <CardContent>
                                                <Typography variant="h6" fontWeight="bold" mb={2}>
                                                    Ticket URLs
                                                </Typography>

                                                <Stack spacing={1.5}>
                                                    {ticketUrls.map((item: any) => (
                                                        <Box
                                                            key={item.id}
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            sx={{
                                                                border: "1px solid #E5EAF2",
                                                                borderRadius: 2,
                                                                px: 2,
                                                                py: 1.5,
                                                            }}
                                                        >
                                                            <Typography>{item.url}</Typography>
                                                            <IconButton color="error" onClick={() => handleDeleteTicketUrl(item.id)}>
                                                                <Delete />
                                                            </IconButton>
                                                        </Box>
                                                    ))}

                                                    <Divider sx={{ my: 1 }} />

                                                    <Box display="flex" gap={1}>
                                                        <TextField
                                                            fullWidth
                                                            placeholder="Enter ticket URL"
                                                            value={newTicketUrl}
                                                            onChange={(e) => setNewTicketUrl(e.target.value)}
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<Add />}
                                                            onClick={handleAddTicketUrl}
                                                            disabled={addingTicket}
                                                        >
                                                            Add
                                                        </Button>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Card sx={{ boxShadow: 4 }}>
                                            <CardContent>
                                                <Typography variant="h6" fontWeight="bold" mb={2}>
                                                    Linked Theatres
                                                </Typography>

                                                <Stack spacing={1.5}>
                                                    {linkedTheatres.length === 0 ? (
                                                        <Typography color="text.secondary">No theatres linked yet.</Typography>
                                                    ) : (
                                                        linkedTheatres.map((theatre: any) => (
                                                            <Box
                                                                key={theatre.id}
                                                                display="flex"
                                                                justifyContent="space-between"
                                                                alignItems="center"
                                                                sx={{
                                                                    border: "1px solid #E5EAF2",
                                                                    borderRadius: 2,
                                                                    px: 2,
                                                                    py: 1.5,
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Typography fontWeight="bold">{theatre.name}</Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {theatre.type} • {theatre.is_active ? "Active" : "Inactive"}
                                                                    </Typography>
                                                                </Box>

                                                                <Stack direction="row" spacing={1}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        onClick={() => navigate(`/theatres/${theatre.id}`)}
                                                                    >
                                                                        View
                                                                    </Button>
                                                                    <Button
                                                                        color="error"
                                                                        variant="outlined"
                                                                        onClick={() => handleUnlinkTheatre(theatre.id)}
                                                                    >
                                                                        Unlink
                                                                    </Button>
                                                                </Stack>
                                                            </Box>
                                                        ))
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Form>
                )}
            </Formik>
        </DefaultPage>
    );
}